import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Swal from 'sweetalert2'
import clsx from 'clsx'
import useAccessToken from '../../hooks/AccessToken'
import { mutate } from 'swr'
import { useIntl } from 'react-intl'
import Select from 'react-select'
import { SelectOption } from '../../interfaces/SelectOption'
import { usePipelineStore } from '../../stores/PipelineStore'
import {
  base64ToFile,
  convertHtmlToWhatsappFormat,
  handleCaps,
} from '../../functions/general'
import ReactQuill from 'react-quill'
import { shallowEqual, useSelector } from 'react-redux'
import { useCustomeStore } from '../../stores/CustomeStore'
import { UserModel } from '../../app/modules/auth/models/UserModel'
import { RootState } from '../../setup'
import { useSettingsStore } from '../../stores/SettingsStore'
import {
  InvoicePages,
  InvoiceMultiPageSetting,
} from '../../interfaces/Invoices'
import { Modal } from 'react-bootstrap'
import { insertAttachment } from '../../api/ResourceAPI'
import { updateInvoices } from '../../api/InvoicesCRUD'
import { getInvoiceDetail, handleModalClosed, setCustomFieldVariables } from './InvoiceUtils'
import { InvoiceMultiInputImage } from './InvoiceInputPage'
import ContainerDnDInvoice from './ContainerDnDInvoice'
import { InvoicePreview } from './PreviewInvoice'
import { InvoiceSteps, LoadingInvoiceContent } from './InvoiceComponentParts'

interface UpdateInvoicesProps {
  showUpdateModal: boolean
  setShowUpdateModal: React.Dispatch<React.SetStateAction<boolean>>
  invoice: InvoiceMultiPageSetting
}

const API_URL = process.env.REACT_APP_API_URL

export const UpdateInvoice: FC<UpdateInvoicesProps> = ({
  showUpdateModal,
  setShowUpdateModal,
  invoice,
}) => {
  const { multiPipelines } = usePipelineStore()
  const { settings } = useSettingsStore()
  const token = useAccessToken()
  const [multiPages, setMultiPages] = useState<InvoicePages[] | null>(null)
  const [previewMultiPages, setPreviewMultiPages] = useState<InvoicePages[] | null>(null)
  const [extractPages, setExtractPages] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [modalBtnLoading, setModalBtnLoading] = useState(false)
  const modalRef = useRef<HTMLDivElement | null>(null)
  const [isBack, setIsBack] = useState(false)
  const user: UserModel = useSelector<RootState>(({ auth }) => auth.user, shallowEqual) as UserModel
  const { companyId } = useCustomeStore()
  const [stepForm, setStepForm] = useState<number>(0)
  const [column, setColumn] = useState<any[]>([])
  const quillRef = useRef<ReactQuill>(null)
  const [dataProgress, setDataProgress] = useState<SelectOption[]>([])
  const intl = useIntl()

  const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = '';
  }, []);

  useEffect(() => {
    if (showUpdateModal) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [showUpdateModal, handleBeforeUnload]);

  const formSchema = Yup.object().shape({
    id: Yup.string().required('ID is required'),
    name: Yup.string().required(
      intl.formatMessage({ id: 'FORM.VALIDATION.CHOOSE' }, { title: 'Nama' })
    ),
    caption: Yup.string(),
    progressId: Yup.string()
      .uuid('Invalid progress id')
      .nullable()
      .required(intl.formatMessage({ id: 'FORM.VALIDATION.CHOOSE' }, { title: 'Progress' })),
    leadTemplateId: Yup.string().required(
      intl.formatMessage({ id: 'FORM.VALIDATION.CHOOSE' }, { title: 'Pipe' })
    ),
    format: Yup.string(),
  })

  const formik = useFormik({
    initialValues: {
      id: '',
      name: '',
      caption: '',
      progressId: '',
      leadTemplateId: invoice.leadTemplate!.id,
      format: 'pdf',
      backgroundImage: [],
    },
    validateOnChange: true,
    enableReinitialize: true,
    validationSchema: formSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      // ----- Set Form
      if (stepForm === 0) {
        setStepForm(1)
      }
      // ----- Set Background Image
      if (stepForm === 1) {
        if (multiPages?.length === 0 || multiPages === null) return
        let approveImages: number = 0
        for (let index = 0; index <= multiPages?.length; index++) {
          if (!multiPages[index].imageFile) {
            formik.setErrors({
              backgroundImage: intl.formatMessage(
                { id: 'INVOICE.FORM.FILE_NOT_FOUND' },
                { title: multiPages[index].name }
              ),
            })
          }

          if (
            multiPages[index].imageFile!.width >= 400 &&
            multiPages[index].imageFile!.height >= 400 &&
            multiPages[index].imageFile!.width <= 2000 &&
            multiPages[index].imageFile!.height <= 2000
          ) {
            approveImages++
            if (approveImages === multiPages?.length) {
              setStepForm(2)
            }
          } else {
            formik.setErrors({
              backgroundImage: intl.formatMessage(
                { id: 'FORM.VALIDATION.SIZE' },
                { file: multiPages[index].name, min: '400x400', max: '2000x2000' }
              ),
            })
          }
        }
      }
      // ----- Edit per Page
      if (multiPages) {
        for (let index = 2; index < multiPages!.length + 1; index++) {
          let itemsIndex = index - 2
          if (stepForm === index) {
            if (!multiPages[itemsIndex].fields || multiPages[itemsIndex].fields.length === 0) {
              Swal.fire({
                icon: 'error',
                title: intl.formatMessage({ id: 'INVOICE.EMPTY' }, { title: intl.formatMessage({ id: 'FORM.LABEL.INVOICE' }) }),
                confirmButtonText: 'Ok',
              })
              return
            }
            setStepForm(index + 1)
          }
        }
        // ----- preview
        if (stepForm === (multiPages!.length + 1)) {
          if (!multiPages[multiPages!.length - 1].fields || multiPages[multiPages!.length - 1].fields.length === 0) {
            Swal.fire({
              icon: 'error',
              title: intl.formatMessage({ id: 'INVOICE.EMPTY' }, { title: intl.formatMessage({ id: 'FORM.LABEL.INVOICE' }) }),
              confirmButtonText: 'Ok',
            })
            return
          }
          setStepForm(stepForm + 1)
        }
      }
      // ----- Submit
      if (stepForm === multiPages!.length + 2) {
        if (multiPages?.length === 0 || multiPages === null) {
          return
        }

        setModalBtnLoading(true)
        setSubmitting(true)

        try {
          const convertedPagesDND_Data = await Promise.all(
            multiPages.map(async (items) => {
              const generateDnDType = items.fields.map((item) => {
                const { dnd_data, model, ...rest } = item
                let newKey = 'dnd_data' // Default key

                if (model === 'pipeline') {
                  newKey = 'base'
                } else if (model === 'pipeline-multi-product') {
                  newKey = 'list'
                } else if (model === 'pipeline-cf' && dnd_data.reference !== 'customFields') {
                  newKey = 'base'
                } else if (model === 'pipeline-cf' && dnd_data.reference === 'customFields') { // TODO: (UNUSED)
                  newKey = 'list'
                }

                return {
                  ...rest,
                  [newKey]: dnd_data,
                }
              })

              const reqImageURL = await insertAttachment(base64ToFile(items.imageFile!.value, 'image.png'), 'INVOICE', token)
              const templatePath = reqImageURL.data.data[0]
              return {
                ...items,
                templatePath,
                fields: generateDnDType
              }
            })
          )

          const response = await updateInvoices(
            values.id,
            {
              progressId: values.progressId,
              leadTemplateId: values.leadTemplateId,
              name: values.name,
              caption: convertHtmlToWhatsappFormat(values.caption),
              format: values.format,
              pages: convertedPagesDND_Data
            },
            token
          )

          if (response.data.success) {
            Swal.fire({
              icon: 'success',
              heightAuto: false,
              title: intl.formatMessage({ id: 'FORM.VALIDATION.UPDATE_SUCCESS' }),
              timer: 2000,
              timerProgressBar: true,
            })
            handleModalClose()
            mutate(`${API_URL}invoices/settings`)
          }
        } catch (error: any) {
          Swal.fire({
            icon: 'error',
            title: error.response.data.message,
            confirmButtonText: 'Ok',
          })
        } finally {
          setSubmitting(false)
          setModalBtnLoading(false)
        }
      }
    },
  })

  const handleModalClose = (isSubmit: boolean = false) => {
    setIsBack(false)
    setShowUpdateModal(false)
    setStepForm(0)
    if (!isSubmit) {
      setColumn([])
    }
  }

  useEffect(() => {
    handleModalClosed(modalRef, formik, setShowUpdateModal)
  }, [formik])

  useEffect(() => {
    setCustomFieldVariables(intl, user, settings, companyId, multiPipelines, formik, setColumn)
  }, [multiPipelines])

  useEffect(() => {
    if (invoice && column.length > 0) {
      getInvoiceDetail('update', invoice, token, formik, column, setMultiPages, setIsLoading)
    }
  }, [invoice, column])

  useEffect(() => {
    if (invoice.progress) {
      const data = {
        value: invoice.progress.id,
        label: invoice.progress.name,
      }
      setDataProgress([data])
    }
  }, [invoice])

  useEffect(() => {
    if (multiPages && stepForm > 0 && stepForm === multiPages.length + 2) {
      const setPreviewItem = multiPages.map((item) => {
        return {
          ...item,
          fields: item.fields.map((field) => {
            return {
              ...field,
              dnd_data: {
                ...field.dnd_data,
                previewQtyHtml: field.dnd_data.previewQtyHtml?.replace(/<p>(.*?)<\/p>/g, '<p style="margin-bottom:0px !important; line-height: 8px;">$1</p><div style="margin-top: 6px"></div>') ?? 'xx',
              },
            }
          }),
        }
      })
      setPreviewMultiPages(setPreviewItem)
    }
  }, [stepForm]);

  return (
    <div>
      <Modal
        show={showUpdateModal}
        centered
        backdrop='static'
        keyboard={false}
        size='xl'
      >
        <Modal.Header closeButton onHide={handleModalClose}>
          <Modal.Title>
            {intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })}{' '}
            {intl.formatMessage({ id: 'MENU.INVOICES' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '0px', minHeight: '400px' }}>
          <div
            className='stepper stepper-links d-flex flex-column first m-0'
            id='kt_create_account_stepper'
            data-kt-stepper='true'
          >
            {isLoading ? (
              <LoadingInvoiceContent />
            ) : (
              <form onSubmit={formik.handleSubmit} noValidate>
                <div
                  className={`${stepForm === 0 ? 'current' : 'pending'}`}
                  data-kt-stepper-element='content'
                >
                  <div className='modal-body'>
                    <label className='form-label fs-6 fw-bolder text-dark required'>
                      {intl.formatMessage({ id: 'FORM.LABEL.NAME' })}
                    </label>
                    <div className='input-group mb-3'>
                      <input
                        {...formik.getFieldProps('name')}
                        className={clsx('form-control form-control-lg', {
                          'is-invalid': formik.touched.name && formik.errors.name,
                        })}
                        type='text'
                        name='name'
                        autoComplete='off'
                        onInput={(e) => handleCaps(e)}
                      />
                    </div>
                    {formik.touched.name && formik.errors.name && (
                      <div className='fv-plugins-message-container text-danger'>
                        <span role='alert' className='text-danger'>
                          {formik.errors.name}
                        </span>
                      </div>
                    )}
                    <div className='row gap-4'>
                      <div className='col fv-row fv-plugins-icon-container'>
                        <label className='form-label fs-6 fw-bolder text-dark required'>
                          {intl.formatMessage({ id: 'FORM.LABEL.PIPE' })}
                        </label>
                        <div className='input-group mb-3'>
                          <Select
                            isDisabled={true}
                            openMenuOnClick
                            options={multiPipelines}
                            placeholder={intl.formatMessage({ id: 'FORM.LABEL.PIPE' })}
                            value={multiPipelines.filter((option) =>
                              formik.values.leadTemplateId.includes(option.value)
                            )}
                            onChange={(option) =>
                              formik.handleChange({
                                target: {
                                  name: 'leadTemplateId',
                                  value: option?.value || '',
                                },
                              })
                            }
                            className='w-100'
                            onBlur={() =>
                              formik.values.leadTemplateId === '' &&
                              formik.setFieldTouched('leadTemplateId', true)
                            }
                          />
                          {formik.touched.leadTemplateId && formik.errors.leadTemplateId && (
                            <div className='fv-plugins-message-container text-danger'>
                              <span role='alert' className='text-danger'>
                                {formik.errors.leadTemplateId}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className='col fv-row fv-plugins-icon-container'>
                        <label className='form-label fs-6 fw-bolder text-dark required'>
                          {intl.formatMessage({ id: 'FORM.LABEL.PROGRESSES' })}
                        </label>
                        <div className='input-group mb-3'>
                          <Select
                            isDisabled={true}
                            openMenuOnClick
                            placeholder={intl.formatMessage({ id: 'FORM.LABEL.PROGRESSES' })}
                            value={dataProgress}
                            className='w-100'
                          />
                          {formik.touched.progressId && formik.errors.progressId && (
                            <div className='fv-plugins-message-container text-danger'>
                              <span role='alert' className='text-danger'>
                                {formik.errors.progressId}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <label className='form-label fs-6 fw-bolder text-dark '>
                      {intl.formatMessage({ id: 'FORM.LABEL.CAPTION' })} WhatsApp
                    </label>
                    <div className='input-group mb-3'>
                      <ReactQuill
                        theme='snow'
                        value={formik.values.caption ? formik.values.caption : ''}
                        onChange={(content) => {
                          formik.setFieldValue('caption', content)
                        }}
                        formats={['bold', 'italic', 'strike']} //'underline',
                        modules={{
                          toolbar: [['bold', 'italic', 'strike']], //'underline',
                        }}
                        ref={quillRef}
                        style={{ height: 200, width: '100%' }}
                        // onBlur={() => formik.setFieldTouched('content', true)}
                        className='link mb-10'
                      />
                    </div>
                    {formik.touched.caption && formik.errors.caption && (
                      <div className='fv-plugins-message-container text-danger'>
                        <span role='alert' className='text-danger'>
                          {formik.errors.caption}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={`${stepForm === 1 ? 'current' : 'pending'}`}
                  data-kt-stepper-element='content'
                >
                  <div className='modal-body'>
                    <InvoiceMultiInputImage
                      context='update'
                      multiPages={multiPages}
                      formik={formik}
                      setMultiPages={setMultiPages}
                    />
                  </div>
                </div>
                {multiPages && multiPages.length > 0 && (
                  <>
                    {Array.from({ length: multiPages.length }).map((_, index) => (
                      <div
                        key={index}
                        className={`${stepForm === index + 2 ? 'current' : 'pending'}`}
                        data-kt-stepper-element='content'
                      >
                        <ContainerDnDInvoice
                          routerContext='invoice'
                          stepType='config'
                          column={column}
                          extractPages={extractPages}
                          pageItems={multiPages[index]}
                          setMultiPages={setMultiPages}
                        />
                      </div>
                    ))}

                    <div
                      className={`${stepForm > 0 && stepForm === multiPages.length + 2 ? 'current' : 'pending'
                        } `}
                      data-kt-stepper-element='content'
                    >
                      {previewMultiPages && previewMultiPages.length > 0 && (
                        <InvoicePreview
                          routerContext='invoice'
                          invoice={{
                            id: invoice.id,
                            name: formik.values.name,
                            caption: formik.values.caption,
                            progressId: invoice.progressId,
                            leadTemplateId: invoice.leadTemplateId,
                            format: "pdf",
                            pages: invoice.pages,
                            leadId: invoice.leadId,
                          }}
                          column={column}
                          multiPages={previewMultiPages}
                          extractPages={extractPages}
                          setMultiPages={setMultiPages}
                        />
                      )}
                    </div>
                  </>
                )}
                <InvoiceSteps
                  context='update'
                  stepForm={stepForm}
                  multiPages={multiPages}
                  modalBtnLoading={modalBtnLoading}
                  setExtractPages={setExtractPages}
                  handleBack={() => {
                    if (stepForm > 0) {
                      setIsBack(true)
                      setStepForm(stepForm - 1)
                    }
                  }}
                  handleModalClose={handleModalClose}
                />
              </form>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}
